// Global
import React, { useEffect, useState } from 'react';

// Local
import styles from './RecipeHeader.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import clsx from 'clsx';
import EmbeddedVideo from 'components/helpers/Video/EmbeddedVideo';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';
import { BazaarVoiceRating } from 'components/helpers/BazaarVoice';
import { OneTrustFilter } from 'components/helpers/OneTrust';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import {
  buildSitecoreMediaUrl,
  getExternalLinkUrl,
  parseImageSize,
} from 'components/helpers/Global/SitecoreImage/sitecoreMedia';

export type RecipeHeaderAttribute =
  | 'PrepTime'
  | 'TotalTime'
  | 'IngredientCount'
  | 'ServingsCount'
  | 'CaloriesPerServing'
  | 'SpiceLevel';

export interface RecipeHeaderProps {
  // Image
  imageUrl: string;
  imageAlt: string;

  // Video
  videoId: string;
  videoTitle: string;
  videoUrl?: string;
  videoThumbnailUrl: string;
  videoWithAudioDescriptionURL: string;
  videoTranscriptFileUrl: string;

  // Basic info
  title: string;

  // Timing and serving info
  prepTime: string;
  totalTime: string;
  servingNumber: string;
  caloriesPerServing: string;

  // Bazaarvoice Rating info
  contentId: string;
  ratingAndReviewContentId?: string;

  // Ingredient count
  totalIngredients: number;

  rdpHeaderSettings?: {
    hideRdpHeader?: boolean;
    hideRdpHeaderRatingsAndReviews?: boolean;
    hideRdpHeaderHeadline?: boolean;
    hideRdpHeaderVideo?: boolean;
    hideRdpHeaderAttributes?: boolean;
    hideRdpHeaderAttributeList?: RecipeHeaderAttribute[];
  };
}

export function RecipeHeader(props: RecipeHeaderProps) {
  const {
    imageUrl,
    imageAlt,
    title,
    prepTime,
    totalTime,
    servingNumber,
    caloriesPerServing,
    contentId,
    ratingAndReviewContentId,
    totalIngredients,
    rdpHeaderSettings,
    videoId,
    videoTitle,
    videoWithAudioDescriptionURL,
    videoTranscriptFileUrl,
  } = props;

  const { getDictionaryValue } = useDictionary();

  const {
    hideRdpHeader,
    hideRdpHeaderRatingsAndReviews,
    hideRdpHeaderHeadline,
    hideRdpHeaderVideo,
    hideRdpHeaderAttributes,
    hideRdpHeaderAttributeList,
  } = rdpHeaderSettings || {};
  /*   API-driven video state (with fallback) */
  const [videoData, setVideoData] = useState<{
    videoId?: string;
    videoThumbnailUrl?: string;
    transcriptUrl?: string;
    videoThumbnail?: string;
    videoURL?: string;
    videoThumbnailExternal?: string;
  }>({
    transcriptUrl: videoTranscriptFileUrl,
  });
  const { locale } = useGlobalData();
  const effectiveVideoId = videoData.videoId || videoId;
  const effectiveTranscript = videoData.transcriptUrl;
  const effectiveVideoUrl =
    videoData.videoURL || `https://www.youtube.com/watch?v=${effectiveVideoId}`;
  const effectivideoThumbnail = videoData.videoThumbnailUrl || imageUrl;
  const showVideo = !hideRdpHeaderVideo && Boolean(effectiveVideoId);

  useEffect(() => {
    if (!contentId) return;
    const fetchVideoData = async () => {
      try {
        const response = await fetch(
          `/api/video/video-data?recipeContentId=${contentId}&language=${locale}`
        );

        if (!response.ok) return;
        const data = await response.json();
        const videos = Array.isArray(data?.videos) ? data.videos : [];
        if (videos.length === 0) {
          return;
        }
        const video = videos[0];
        if (!video) return;
        const { width, height } = parseImageSize(video.videoThumbnail?.value);

        setVideoData({
          videoId: video.videoId?.value || '',
          videoThumbnailUrl:
            getExternalLinkUrl(video.videoThumbnailExternal?.value) ||
            buildSitecoreMediaUrl(video.videoThumbnail?.value, {
              host: process.env.NEXT_PUBLIC_SITECORE_HOST,
              width: width ? Number(width) : 560,
              height: height ? Number(height) : 315,
            }),
          videoURL: video.videoURL?.jsonValue?.value,
          transcriptUrl: video.transcriptUrl?.value || '',
        });
      } catch (e) {
        console.error('Video fetch failed', e);
      }
    };

    fetchVideoData();
  }, [contentId, locale]);

  // Early return if header is completely hidden
  if (hideRdpHeader) {
    return null;
  }

  return (
    <div
      id="gmi-recipe-header"
      aria-label={title || ''}
      role="region"
      tabIndex={-1}
      className={`${styles.recipeHeader} printHide`}
    >
      <div className={styles.recipeHeaderSurface}>
        <div className={styles.content}>
          <div className={styles.copyContainer}>
            {(!hideRdpHeaderHeadline || !hideRdpHeaderRatingsAndReviews) && (
              <div className={styles.headerCopy}>
                {!hideRdpHeaderHeadline && title && <h2 className={styles.title}>{title}</h2>}

                {!hideRdpHeaderRatingsAndReviews && (
                  <OneTrustFilter
                    optanonGroups={['2', '3', '4']}
                    enablePlaceholder={false}
                    filteredComponent={
                      <BazaarVoiceRating
                        ratingAndReviewContentId={ratingAndReviewContentId}
                        className={styles.bvRating}
                        showType="rating_summary"
                      />
                    }
                  />
                )}
              </div>
            )}

            {/* Recipe Attributes stats */}
            {!hideRdpHeaderAttributes && (
              <div className={styles.statsContainer}>
                <ul className={styles.statsList}>
                  {prepTime && !hideRdpHeaderAttributeList?.includes('PrepTime') && (
                    <li className={styles.recipeAttributeItem}>
                      <div className={styles.attributeTextGroup}>
                        <span className={styles.attributeLabel}>
                          {getDictionaryValue('Components.Recipe.Attributes.PrepTime')}:
                        </span>
                        <span className={styles.attributeValue}>{prepTime}</span>
                      </div>
                    </li>
                  )}
                  {totalTime && !hideRdpHeaderAttributeList?.includes('TotalTime') && (
                    <li className={styles.recipeAttributeItem}>
                      <div className={styles.attributeTextGroup}>
                        <span className={styles.attributeLabel}>
                          {getDictionaryValue('Components.Recipe.Attributes.TotalTime')}:
                        </span>
                        <span className={styles.attributeValue}>{totalTime}</span>
                      </div>
                    </li>
                  )}
                  {totalIngredients && !hideRdpHeaderAttributeList?.includes('IngredientCount') && (
                    <li className={styles.recipeAttributeItem}>
                      <div className={styles.attributeTextGroup}>
                        <span className={styles.attributeLabel}>
                          {getDictionaryValue('Components.Recipe.Attributes.Ingredients')}:
                        </span>
                        <span className={styles.attributeValue}>{totalIngredients}</span>
                      </div>
                    </li>
                  )}
                  {servingNumber && !hideRdpHeaderAttributeList?.includes('ServingsCount') && (
                    <li className={styles.recipeAttributeItem}>
                      <div className={styles.attributeTextGroup}>
                        <span className={styles.attributeLabel}>
                          {getDictionaryValue('Components.Recipe.Attributes.Servings')}:
                        </span>
                        <span className={styles.attributeValue}>{servingNumber}</span>
                      </div>
                    </li>
                  )}
                  {caloriesPerServing &&
                    !hideRdpHeaderAttributeList?.includes('CaloriesPerServing') && (
                      <li className={styles.recipeAttributeItem}>
                        <div className={styles.attributeTextGroup}>
                          <span className={styles.attributeLabel}>
                            {getDictionaryValue('Components.Recipe.Attributes.CaloriesPerServing')}:
                          </span>
                          <span className={styles.attributeValue}>{caloriesPerServing}</span>
                        </div>
                      </li>
                    )}
                </ul>
              </div>
            )}
          </div>

          {/* Media Section */}
          {(imageUrl || showVideo) && (
            <div
              className={clsx(
                styles.mediaContainer,
                styles[showVideo ? 'withVideo' : 'withoutVideo']
              )}
            >
              {showVideo ? (
                <EmbeddedVideo
                  videoId={effectiveVideoId}
                  videoThumbnailUrl={effectivideoThumbnail}
                  videoTitle={videoTitle}
                  videoUrl={effectiveVideoUrl}
                  videoWithAudioDescriptionURL={videoWithAudioDescriptionURL}
                  videoTranscriptFileUrl={effectiveTranscript}
                  isPortrait={false}
                />
              ) : (
                <EnhancedNextImage
                  src={imageUrl}
                  alt={imageAlt || title}
                  width={560}
                  height={315}
                  className={styles.recipeHeaderImage}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
